import React, { useState, useEffect } from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation,} from 'react-router-dom';
import { Amplify, Auth, Hub } from 'aws-amplify';
import amplifyConfig from './Config/amplifyconfiguration';
import AdminUsers from './Config/AdminUsers';
import routesfe from './Config/RoutesFE';
import { FaBars } from 'react-icons/fa';
import { AwsRum } from 'aws-rum-web';
import { rumConfig, APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION } from './Config/rumConfig';

// Public Pages
import LandingPage from './Landing/LandingPage';
import LoginPage from './Landing/LoginPage';
import SignupPage from './Landing/SignupPage';
import ForgotPasswordPage from './Landing/ForgotPasswordPage';
import PlansPage from './Landing/PlansPage';
import PrivacyPolicyPage from './Landing/PrivacyPolicyPage';
import TermsOfServicePage from './Landing/TermsOfServicePage';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Protected Page
import Home from './UserControls/MainHome';

// Components
import RequireAuth from './Landing/RequireAuth';
import LeftSidebar from './components copy/LeftSidebar';
import LoadingSpinner from './components copy/LoadingSpinner';
import '@aws-amplify/ui-react/styles.css';
import './Styles/App.css';
import './Styles/globals.css';

import NoteforgeAiChat from './pages/NoteforgeAiChat';
import Record from './components copy/Record';
import NoteDetail from './components copy/NoteDetail';
import ProfilePage from './pages/ProfilePage';
import PaymentPage from './UserControls/Payment/PaymentPage';
import PaymentSuccessPage from './UserControls/Payment/PaymentSuccessPage';
import UpgradePage from './pages/UpgradePage';
import PayPalSuccess from './UserControls/Payment/PayPalSuccess';
import PayPalFailure from './UserControls/Payment/PayPalFailure';
import { Container } from 'react-bootstrap';
import UserVerification from './Landing/UserVerification';
import LiveTranscriptAccessWrapper from './components copy/LiveTranscriptAccessWrapper';
import RecordProvider from './components copy/RecordContext';
import { NoteSelectionProvider } from './context/NoteSelectionContext';
import { useParams } from 'react-router-dom';

Amplify.configure(amplifyConfig);

// Layout with sidebar
const AuthenticatedLayout = ({ children, handleSignOut, user, onHomeClick, authIdToken }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Start expanded on large screens, collapsed on smaller screens
  const [isMiniSidebar, setIsMiniSidebar] = useState(window.innerWidth < 1200);

  // Handle window resize to auto-expand/collapse sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setIsMiniSidebar(false); // Expand on large screens
      } else if (window.innerWidth >= 769) {
        setIsMiniSidebar(true); // Collapse on medium screens
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Regex match for /watch/:sessionId and /home/:sessionId
  const hideSidebarRoutes = [
    /^\/watch\/[^/]+$/,
    /^\/payment$/,
    /^\/payment-success$/,
    /^\/payment-failure$/,
    /^\/paypal-success$/,
    /^\/paypal-failure$/
  ];
  const shouldHideSidebar = hideSidebarRoutes.some((pattern) =>
    pattern.test(location.pathname)
  );

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    // Prevent body scroll on mobile when sidebar is open
    if (window.innerWidth <= 768) {
      document.body.classList.toggle('sidebar-open', !sidebarOpen);
    }
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    // Re-enable body scroll
    document.body.classList.remove('sidebar-open');
  };

  const toggleMiniSidebar = () => {
    setIsMiniSidebar(!isMiniSidebar);
  };

  useEffect(() => {
    try {
      if (!window.__awsRumInitialized) {
        window.__awsRumInitialized = true;

        const rum = new AwsRum(
          APPLICATION_ID,
          APPLICATION_VERSION,
          APPLICATION_REGION,
          rumConfig
        );

        window.rum = rum;
        console.log("[RUM] Initialized (unsigned mode)");
      }
    } catch (error) {
      console.error("[RUM] Initialization error:", error);
    }
  }, []);

  return (
    <>
      {/* Mobile Header */}
      {!shouldHideSidebar && (
        <header className="mobile-header">
          <button className="hamburger-btn" onClick={toggleSidebar}>
            <FaBars size={24} />
          </button>
          <h1 className="mobile-title">NoteForge</h1>
        </header>
      )}

      <div className="d-flex" style={{ minHeight: '100vh' }}>
        {!shouldHideSidebar && (
        <LeftSidebar
            handleSignOut={handleSignOut}
            userName={user?.attributes?.name}
            userEmail={user?.attributes?.email}
            onHomeClick={onHomeClick}
            isOpen={sidebarOpen}
            onClose={closeSidebar}
            isMiniSidebar={window.innerWidth > 768 ? isMiniSidebar : false}
            onToggleMini={toggleMiniSidebar}
            authIdToken={authIdToken}
          />
        )}
        <div className={`main-content${shouldHideSidebar ? ' no-sidebar' : (window.innerWidth > 768 && isMiniSidebar ? ' mini-sidebar' : '')}`} style={{ flex: 1 }}>{children}</div>
      </div>
    </>
  );
};


// Logout Route
const Logout = ({ onLogout }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const doLogout = async () => {
      await Auth.signOut();
      onLogout();
      navigate('/');
    };
    doLogout();
  }, [navigate, onLogout]);

  return <div>Logging out...</div>;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

const AppContent = ({ user, userEmail, authIdToken, handleSignOut, setUser }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get('redirect') || routesfe.HOME;

  return (
    <>
      <ScrollToTop />
      {/* Make container fluid so full-bleed sections can touch the viewport edges */}
      <Container fluid className="px-0">
        <Routes>
          {/* Public Routes */}
          <Route
            path={routesfe.LANDING}
            element={user ? <Navigate to={routesfe.HOME} replace /> : <LandingPage hideHeader={!!user} />}
          />

          <Route
            path={routesfe.LOGIN}
            element={
              user ? (
                (() => {
                  // If redirected from subscribe, go to payment page with plan info
                  const redirectPath = location.state?.redirect;
                  const selectedPlan = location.state?.selectedPlan;
                  if (redirectPath === routesfe.PAYMENT && selectedPlan) {
                    return <Navigate to={redirectPath} state={{ selectedPlan }} replace />;
                  }
                  return <Navigate
                    to={
                      location.state?.from?.pathname ||
                      new URLSearchParams(location.search).get('redirect') ||
                      routesfe.HOME
                    }
                    replace
                  />;
                })()
              ) : (
                <LoginPage />
              )
            }
          />

          <Route
            path={routesfe.SIGNUP}
            element={user ? <Navigate to={routesfe.HOME} replace /> :  <SignupPage />}
          />

          <Route
            path={routesfe.FORGOT_PASSWORD}
            element={user ? <Navigate to={routesfe.HOME} replace /> : <ForgotPasswordPage />}
          />

          <Route
            path={routesfe.USER_VERIFICATION}
            element={<UserVerification />}
          />

          <Route
            path={routesfe.PLANS}
            element={<PlansPage />}
          />

          <Route
            path={routesfe.PRIVACY_POLICY}
            element={<PrivacyPolicyPage />}
          />

          <Route
            path={routesfe.TERMS_OF_SERVICE}
            element={<TermsOfServicePage />}
          />

          {/* Protected Route */}
          <Route
            path={routesfe.HOME}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <Home
                    userEmail={userEmail}
                    userDisplayName={user?.attributes?.name}
                    authIdToken={user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null}
                    handleSignOut={handleSignOut}
                    isAdmin={AdminUsers.usersList.includes(user?.attributes?.email)}
                  />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />
          <Route
            path={routesfe.CONVERSATION_TRANSCRIPT}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <Home
                    userEmail={userEmail}
                    userDisplayName={user?.attributes?.name}
                    authIdToken={user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null}
                    handleSignOut={handleSignOut}
                    isAdmin={AdminUsers.usersList.includes(user?.attributes?.email)}
                  />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />
          <Route
            path={routesfe.CONVERSATION_SUMMARY}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <Home
                    userEmail={userEmail}
                    userDisplayName={user?.attributes?.name}
                    authIdToken={user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null}
                    handleSignOut={handleSignOut}
                    isAdmin={AdminUsers.usersList.includes(user?.attributes?.email)}
                  />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />
          <Route
            path={routesfe.CONVERSATION_ANNOTATE}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <Home
                    userEmail={userEmail}
                    userDisplayName={user?.attributes?.name}
                    authIdToken={user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null}
                    handleSignOut={handleSignOut}
                    isAdmin={AdminUsers.usersList.includes(user?.attributes?.email)}
                  />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />

          {/* Upgrade Route - Protected, full-width without sidebar */}
          <Route
            path={routesfe.UPGRADE}
            element={
              <RequireAuth user={user}>
                <UpgradePage authIdToken={authIdToken} />
              </RequireAuth>
            }
          />

          {/* Payment Route - Protected but without sidebar */}
          <Route 
            path={routesfe.PAYMENT} 
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <PaymentPage />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />

          {/* Payment Success Route - Protected but without sidebar */}
          <Route 
            path={routesfe.PAYMENT_SUCCESS} 
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <PaymentSuccessPage onUserUpdate={setUser} />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />

          {/* PayPal Success Route - Protected but without sidebar */}
          <Route
            path={routesfe.PAYPAL_SUCCESS}
            element={
              <RequireAuth user={user}>
                <PayPalSuccess onUserUpdate={setUser} />
              </RequireAuth>
            }
          />

          {/* PayPal Failure Route - Protected but without sidebar */}
          <Route
            path={routesfe.PAYPAL_FAILURE}
            element={
              <RequireAuth user={user}>
                <PayPalFailure />
              </RequireAuth>
            }
          />

          {/* Other Protected Pages */}
          <Route path={routesfe.CHAT}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <NoteforgeAiChat />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />

          <Route path={routesfe.RECORD}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <Record
                    authIdToken={user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null}
                  />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />

          <Route
            path={routesfe.RECORD_CONVERSATION}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut} authIdToken={authIdToken}>
                  <Record
                    authIdToken={user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null}
                  />
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />

          <Route
            path={routesfe.LIVETRANSCRIPT}
            element={<LiveTranscriptAccessWrapper user={user} />}
          />

          <Route path={routesfe.PROFILE}
            element={
              <RequireAuth user={user}>
                <AuthenticatedLayout user={user} handleSignOut={handleSignOut}>
                  <ProfilePage/>
                </AuthenticatedLayout>
              </RequireAuth>
            }
          />
          
          <Route path={routesfe.NOTE} element={<NoteDetail />} />

          {/* Logout */}
          <Route path={routesfe.LOGOUT} element={<Logout onLogout={handleSignOut} />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to={routesfe.LANDING} replace />} />
        </Routes>
      </Container>
    </>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [authIdToken, setAuthIdToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // On initial mount: check if already signed in
    Auth.currentAuthenticatedUser()
      .then(user => {
        setUser(user);
        setUserEmail(user.attributes.email);
        setAuthIdToken(user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));

    // Listen for future sign-in / sign-out events
    const listener = ({ payload: { event } }) => {
      if (event === 'signIn') {
        Auth.currentAuthenticatedUser({ bypassCache: true })
          .then(user => {
            setUser(user);
            setUserEmail(user.attributes.email);
            setAuthIdToken(user && user.signInUserSession && user.signInUserSession.idToken ? user.signInUserSession.idToken.jwtToken : null);
          })
          .catch(() => setUser(null));
      }
      if (event === 'signOut') {
        setUser(null);
      }
    };
    Hub.listen('auth', listener);

    return () => {
      Hub.remove('auth', listener);
    };
  }, []);

  const handleSignOut = async () => {
    await Auth.signOut();
    localStorage.removeItem('loginExpiry');
    localStorage.removeItem('user');
    // Clear payment sessionStorage (plan/form/method)
    sessionStorage.removeItem('nf_checkout_plan');
    sessionStorage.removeItem('nf_checkout_billing');
    sessionStorage.removeItem('nf_checkout_method');
    sessionStorage.removeItem('noteforge_trial_activated');
    setUser(null);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <RecordProvider>
      <NoteSelectionProvider>
        <Router>
          <AppContent
            user={user}
            userEmail={userEmail}
            authIdToken={authIdToken}
            handleSignOut={handleSignOut}
            setUser={setUser}
          />
        </Router>
      </NoteSelectionProvider>
    </RecordProvider>
  );
}

export default App;
